/**
 * tenant-modules — the tenant's deployed modules, from getTenantProfile (CC 0130).
 *
 * Read once per session and shared. `null` means unknown: still loading, the read
 * failed, this user may not make it, or the deployment hasn't recorded its modules.
 * Callers keep today's behaviour on null, and hide a surface only when the list is
 * KNOWN and lacks the module.
 *
 * getTenantProfile is read under branding.read (the module list is not a settings secret: event roles
 * need it to know which surfaces the tenant composes; CC, 2026-10-04). It is requested, its answer is
 * accepted, and a cached answer is handed out only while someone is signed in, the
 * tenant is CONFIRMED (getConfirmedTenantSlug() is null during a pending workspace
 * switch, so nothing is requested for either tenant while the chrome shows the
 * incoming one), and the person the app is acting as holds branding.read. That is
 * checked when the read starts, when its answer lands and whenever the answer is
 * read. Otherwise the answer is null and nothing is cached, so an administrator who
 * signs in later in the same page still gets a read.
 *
 * Each answer is filed under the session it was read for: the confirmed tenant, the
 * tenant generation, the signed-in and acted-as identity, the capability, and an
 * epoch that moves whenever any of those changes or a switch starts. An earlier
 * session's read neither fills the cache nor reaches a hook. The fence is a
 * module-level access subscription, so sign-out, a new sign-in, View as and
 * capability changes clear the answer even when no screen that uses it is mounted.
 *
 * Carries the platform-side tenant fence forward (drop-2026-1002c): a switch purges
 * synchronously and requests nothing; the confirmed change re-reads for mounted users.
 */
import { useEffect, useState } from "react";
import { getTenantProfile } from "@v0y4ge/api-client";
import { studioRequestOptions } from "./studio-config";
import {
  can, getConfirmedTenantSlug, getImpersonation, getRealUser, getTenantGeneration, getUser,
  isSignedIn, onTenantChange, onTenantSwitching, subscribeAccess,
} from "../shell/access";

/** The capability getTenantProfile is authorised under (branding.read since CC, 2026-10-04; it was
 *  settings.read, which no event role holds, so their module list stayed unknown). */
const PROFILE_CAPABILITY = "branding.read";

type Modules = string[] | null;
type Read = { scope: string; promise: Promise<Modules> };

let epoch = 0;
let cached: { scope: string; value: Modules } | undefined;
let pending: Read | null = null;
const listeners = new Set<() => void>();

/** Everything an answer belongs to, apart from the epoch. */
function sessionFingerprint(): string {
  const real = getRealUser();
  const acting = getUser();
  const viewAs = getImpersonation();
  return JSON.stringify([
    isSignedIn(), getConfirmedTenantSlug(), getTenantGeneration(),
    real.id ?? "", real.email ?? "", acting.id ?? "", acting.email ?? "",
    viewAs ? viewAs.startedAt : 0, can(PROFILE_CAPABILITY),
  ]);
}

export type TenantProfileAccess =
  | { kind: "denied" }
  | { kind: "waiting" }
  | { kind: "allowed"; scope: string };

/** Whether the current session may read the tenant profile right now. `denied`:
 *  signed out, or the acted-as user lacks branding.read. `waiting`: no confirmed
 *  tenant yet (first load, or a workspace switch in flight). `allowed` carries the
 *  scope a read must still match when its answer lands. */
export function tenantProfileAccess(): TenantProfileAccess {
  if (!isSignedIn() || !can(PROFILE_CAPABILITY)) return { kind: "denied" };
  if (getConfirmedTenantSlug() === null) return { kind: "waiting" };
  return { kind: "allowed", scope: `${epoch}:${sessionFingerprint()}` };
}

function currentScope(): string | null {
  const access = tenantProfileAccess();
  return access.kind === "allowed" ? access.scope : null;
}

function emit(): void { listeners.forEach((fn) => fn()); }

function invalidate(): void {
  const held = cached !== undefined || pending !== null;
  epoch++;
  cached = undefined;
  pending = null;
  if (held) emit();
}

let lastFingerprint = sessionFingerprint();
let reloadQueued = false;

/** Re-read for mounted users once the current notification has finished. Never
 *  requests while the session is denied or waiting. */
function reloadSoon(): void {
  if (reloadQueued || !listeners.size) return;
  reloadQueued = true;
  queueMicrotask(() => {
    reloadQueued = false;
    if (listeners.size && currentScope() !== null) void load();
  });
}

function fence(): void {
  const fingerprint = sessionFingerprint();
  if (fingerprint === lastFingerprint) return;
  lastFingerprint = fingerprint;
  invalidate();
  reloadSoon();
}

subscribeAccess(fence);
onTenantSwitching(() => { invalidate(); lastFingerprint = sessionFingerprint(); });
onTenantChange(fence);

function parseModules(r: unknown): Modules {
  const p = ((r as { data?: unknown })?.data ?? r) as { modules?: unknown } | null;
  return p && Array.isArray(p.modules) ? (p.modules as unknown[]).filter((x): x is string => typeof x === "string") : null;
}

function settle(read: Read, value: Modules): Modules {
  if (pending === read) pending = null;
  if (currentScope() !== read.scope) return null;
  cached = { scope: read.scope, value };
  emit();
  return value;
}

function load(): Promise<Modules> {
  const scope = currentScope();
  if (scope === null) return Promise.resolve(null);
  if (cached?.scope === scope) return Promise.resolve(cached.value);
  if (pending?.scope === scope) return pending.promise;
  let request: Promise<unknown>;
  try { request = getTenantProfile(studioRequestOptions("nodes.tenants") as never) as Promise<unknown>; }
  catch (e) { request = Promise.reject(e); }
  const read: Read = { scope, promise: Promise.resolve(null) };
  read.promise = request.then((r) => settle(read, parseModules(r)), () => settle(read, null));
  pending = read;
  return read.promise;
}

/** The current answer, without a read. Null unless it was read for this session. */
export function tenantModulesNow(): string[] | null {
  const scope = currentScope();
  return scope !== null && cached?.scope === scope ? cached.value : null;
}

/** True only when the module list is known and lacks `module`. */
export function moduleKnownAbsent(module: string, mods: string[] | null = tenantModulesNow()): boolean {
  return Array.isArray(mods) && !mods.includes(module);
}

export function useTenantModules(): string[] | null {
  const [m, setM] = useState<string[] | null>(() => tenantModulesNow());
  useEffect(() => {
    let live = true;
    const push = () => { if (live) setM(tenantModulesNow()); };
    listeners.add(push);
    push();
    void load().then(push);
    return () => { live = false; listeners.delete(push); };
  }, []);
  return m;
}
